package dev.lingvo.site.components.review

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.ReviewItemDto
import dev.lingvo.shared.TranslationDto
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import dev.lingvo.site.i18n.t

private sealed interface LocalizedReviewValuePresentation {
    data class Scalar(val value: String) : LocalizedReviewValuePresentation
    data class Plural(val forms: Map<PluralCategory, String>) : LocalizedReviewValuePresentation
    data object Invalid : LocalizedReviewValuePresentation
}

private enum class LocalizedReviewValueKind {
    SCALAR,
    PLURAL,
}

/**
 * One stored localized value, without normalization or display-time character insertion.
 *
 * `white-space: break-spaces` is deliberate: `pre-wrap` preserves line breaks but can still discard
 * end-of-line spaces during layout. Review is the boundary where leading, repeated, trailing and
 * newline whitespace must remain the exact payload somebody is about to approve.
 */
@Composable
internal fun LocalizedReviewValue(
    locale: String,
    value: String?,
    pluralForms: Map<PluralCategory, String>?,
    hook: String,
    modifier: Modifier = Modifier,
) {
    val presentation = localizedReviewValue(value, pluralForms)
    val kind = when (presentation) {
        is LocalizedReviewValuePresentation.Scalar -> "scalar"
        is LocalizedReviewValuePresentation.Plural -> "plural"
        LocalizedReviewValuePresentation.Invalid -> "invalid"
    }
    Column(
        modifier
            .fillMaxWidth()
            .gap(Space.SM)
            .attr("data-review-localized-value", hook)
            .attr("data-review-value-kind", kind),
    ) {
        when (presentation) {
            is LocalizedReviewValuePresentation.Scalar -> ReviewValueText(locale, presentation.value)
            is LocalizedReviewValuePresentation.Plural -> {
                PluralCategory.entries.forEach { category ->
                    presentation.forms[category]?.let { form ->
                        ReviewPluralForm(locale, category, form)
                    }
                }
            }
            LocalizedReviewValuePresentation.Invalid -> LAlert(
                title = t("review.localized_review_value.value_cannot_be_reviewed_safely", "Value cannot be reviewed safely"),
                description = t("review.localized_review_value.this_response_mixes_or_omits_scalar", "This response mixes or omits scalar and plural data. Reload before approving it."),
                variant = LAlertVariant.DANGER,
            )
        }
    }
}

@Composable
private fun ReviewPluralForm(locale: String, category: PluralCategory, value: String) {
    val palette = ColorMode.current.toSitePalette()
    Column(
        Modifier
            .fillMaxWidth()
            .gap(Space.XS)
            .padding(Space.MD)
            .backgroundColor(palette.surfaceSunken)
            .border(1.px, LineStyle.Solid, palette.border)
            .borderRadius(Radius.Control)
            .attr("data-review-plural-form", category.name.lowercase()),
    ) {
        Span(
            Modifier
                .color(palette.textSecondary)
                .fontSize(TypeScale.Small)
                .fontWeight(650)
                .toAttrs(),
        ) {
            Text(category.name.lowercase().replaceFirstChar(Char::uppercase))
        }
        ReviewValueText(locale, value)
    }
}

@Composable
private fun ReviewValueText(locale: String, value: String) {
    Div(
        Modifier
            .fillMaxWidth()
            .fontSize(TypeScale.Reading)
            .styleModifier {
                property("overflow-wrap", "anywhere")
                property("tab-size", "4")
                property("unicode-bidi", "plaintext")
                property("user-select", "text")
                property("white-space", "break-spaces")
            }
            .toAttrs {
                attr("data-review-value-text", "true")
                attr("lang", locale)
                attr("dir", "auto")
            },
    ) {
        Text(value)
    }
}

/** True only when source and submitted values can be shown exactly and use the same value shape. */
internal fun reviewPayloadIsRepresentable(item: ReviewItemDto): Boolean {
    val sourceKind = localizedReviewValueKind(item.sourceValue, item.sourcePluralForms)
    val targetKind = localizedReviewValueKind(item.targetValue, item.targetPluralForms)
    return sourceKind != null && sourceKind == targetKind
}

/** The stale action is safe only after both versions of the submitted value can be compared. */
internal fun staleReviewPayloadIsRepresentable(item: ReviewItemDto, current: TranslationDto): Boolean {
    if (!reviewPayloadIsRepresentable(item)) return false
    return localizedReviewValueKind(item.targetValue, item.targetPluralForms) ==
        localizedReviewValueKind(current.value, current.pluralForms)
}

private fun localizedReviewValueKind(
    value: String?,
    pluralForms: Map<PluralCategory, String>?,
): LocalizedReviewValueKind? = when {
    value != null && pluralForms == null -> LocalizedReviewValueKind.SCALAR
    value == null && !pluralForms.isNullOrEmpty() -> LocalizedReviewValueKind.PLURAL
    else -> null
}

private fun localizedReviewValue(
    value: String?,
    pluralForms: Map<PluralCategory, String>?,
): LocalizedReviewValuePresentation = when (localizedReviewValueKind(value, pluralForms)) {
    LocalizedReviewValueKind.SCALAR -> LocalizedReviewValuePresentation.Scalar(value.orEmpty())
    LocalizedReviewValueKind.PLURAL -> LocalizedReviewValuePresentation.Plural(pluralForms.orEmpty())
    null -> LocalizedReviewValuePresentation.Invalid
}
